import PropTypes from "prop-types"
import React, { createContext, useEffect, useMemo, useState } from "react"
import { useInfiniteQuery, useQueryClient } from "react-query"
import { collapseReservationSalesAndDates } from "src/main/Billing/Items/helpers"

import { queryProductSaleTxns } from "src/api/Billing/Items"
import { queryInvoices } from "src/api/Billing/Payments"

import usePollingInfiniteQuery from "src/hooks/use_polling_infinite_query"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import {
  INITIAL_INVOICE_REFETCH_INTERVAL,
  INVOICE_REFETCH_BACKOFF_MULTIPLIER,
  MAX_INVOICE_REFETCH_INTERVAL,
} from "./constants"
import { invoicesIncludeProcessingCardPayments } from "./helpers"

export const SharedBillingContext = createContext(null)

const SharedBillingContextProvider = ({
  children,
  reservationId,
  billingContainerId = null,
  contractQuoteId,
  editable,
  contractSurchargeEligible,
  lateFee: persistedLateFee,
  cardReaders = [],
  enableBluetoothCardReader = false,
}) => {
  const queryClient = useQueryClient()
  const marinaSlug = getCurrentMarinaSlug()

  const {
    isFetching: isLoadingItems,
    isError: isErrorItems,
    data: itemsData,
    fetchNextPage: fetchNextTxnPage,
    hasNextPage: hasNextTxnPage,
    isFetchingNextPage: isFetchingNextTxnPage,
  } = useInfiniteQuery({
    queryKey: ["productSaleTxns", marinaSlug, reservationId],
    queryFn: ({ pageParam = 1 }) =>
      queryProductSaleTxns({
        marinaSlug,
        reservationId,
        page: pageParam,
        includeInvoices: true,
      }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length) {
        return pages.length + 1
      }
    },
    select: (data) => {
      return data?.pages ? data.pages.flatMap((page) => page) : undefined
    },
    refetchOnWindowFocus: false,
  })

  const {
    isLoading: isLoadingInvoices,
    isError: isErrorInvoices,
    flattenedData: invoices,
    fetchNextPage: fetchNextInvoicePage,
    hasNextPage: hasNextInvoicePage,
    isFetchingNextPage: isFetchingNextInvoicePage,
    resetRefetchInterval: resetInvoiceRefetchInterval,
  } = usePollingInfiniteQuery({
    queryKey: ["invoices", contractQuoteId],
    queryFn: ({ pageParam = 1 }) =>
      queryInvoices({ marinaSlug, reservationId, page: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length) {
        return pages.length + 1
      }
    },
    shouldPoll: invoicesIncludeProcessingCardPayments,
    initialInterval: INITIAL_INVOICE_REFETCH_INTERVAL,
    maxInterval: MAX_INVOICE_REFETCH_INTERVAL,
    backoffMultiplier: INVOICE_REFETCH_BACKOFF_MULTIPLIER,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!isFetchingNextTxnPage && hasNextTxnPage) {
      fetchNextTxnPage()
    }
  }, [isFetchingNextTxnPage, hasNextTxnPage, fetchNextTxnPage])

  useEffect(() => {
    if (!isFetchingNextInvoicePage && hasNextInvoicePage) {
      fetchNextInvoicePage()
    }
  }, [isFetchingNextInvoicePage, hasNextInvoicePage, fetchNextInvoicePage])

  const refreshItems = () => {
    queryClient.refetchQueries({
      queryKey: ["productSaleTxns", marinaSlug, reservationId],
    })
  }

  const refreshPayments = () => {
    queryClient.refetchQueries({
      queryKey: ["invoices", contractQuoteId],
    })
    // Previous refetches may have caused the refetch interval to go up to 30 seconds.
    // If we are refreshing the payments, we should reset the refetch interval.
    resetInvoiceRefetchInterval()
  }

  const flattenedItems = useMemo(() => {
    return itemsData ? collapseReservationSalesAndDates(itemsData) : undefined
  }, [itemsData])

  const [lateFee, setLateFee] = useState(persistedLateFee)

  return (
    <SharedBillingContext.Provider
      value={{
        reservationId,
        billingContainerId,
        contractQuoteId,
        items: {
          data: itemsData,
          collapsedData: flattenedItems,
          isLoading: isLoadingItems || hasNextTxnPage !== false,
          isError: isErrorItems,
        },
        invoices: {
          data: invoices,
          isLoading: isLoadingInvoices || hasNextInvoicePage !== false,
          isError: isErrorInvoices,
        },
        refreshItems,
        refreshPayments,
        editable,
        contractSurchargeEligible,
        lateFee,
        setLateFee,
        cardReaders,
        enableBluetoothCardReader,
      }}
    >
      {children}
    </SharedBillingContext.Provider>
  )
}

SharedBillingContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  reservationId: PropTypes.number.isRequired,
  billingContainerId: PropTypes.number,
  contractQuoteId: PropTypes.number,
  editable: PropTypes.bool.isRequired,
  contractSurchargeEligible: PropTypes.bool,
  lateFee: PropTypes.shape({
    amount: PropTypes.number,
    gracePeriodDays: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    percentAmount: PropTypes.number,
    productId: PropTypes.string.isRequired,
    reservationId: PropTypes.number.isRequired,
  }),
  cardReaders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      displayName: PropTypes.string,
    })
  ),
  enableBluetoothCardReader: PropTypes.bool,
}

export default SharedBillingContextProvider
